import React, { useState, useEffect } from 'react';
import { campaignsAPI, templatesAPI, buyerGroupsAPI, sendingIntelligenceAPI } from '../services/api';
import SendingAdviceCard from './SendingAdviceCard';
import './SimpleCampaignForm.css';

const SimpleCampaignForm = ({ onClose, onSuccess, campaign = null, onOpenTemplateEditor = null, previewTemplateOverrides = null }) => {
  const isEditMode = !!campaign;

  const [campaignName, setCampaignName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('');
  const [scheduledStartTime, setScheduledStartTime] = useState('');
  const [customizeTemplate, setCustomizeTemplate] = useState(false);
  const [customSubject, setCustomSubject] = useState('');
  const [customBody, setCustomBody] = useState('');
  const [customMessages, setCustomMessages] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState('');
  const [rampEnabled, setRampEnabled] = useState(false);
  const [prioritizeEnabled, setPrioritizeEnabled] = useState(false);
  const [existingRampState, setExistingRampState] = useState(null);
  const [adviceFromEmail, setAdviceFromEmail] = useState(null);

  // Load campaign data if in edit mode
  useEffect(() => {
    if (isEditMode && campaign) {
      setCampaignName(campaign.name || '');
      setDescription(campaign.description || '');
      setSelectedTemplate(campaign.template_id ? String(campaign.template_id) : '');
      setSelectedGroup(campaign.group_id ? String(campaign.group_id) : '');
      setScheduledStartTime(campaign.start_time || '');
      setCustomizeTemplate(!!campaign.template_overrides);
      setRampEnabled(!!campaign.ramp_enabled);
      setPrioritizeEnabled(!!campaign.prioritize_deliverable_enabled);
    }
  }, [isEditMode, campaign]);

  const multiTemplates = templates.filter(
    (template) => Array.isArray(template.json_config?.messages) && template.json_config.messages.length > 1
  );
  const singleTemplates = templates.filter(
    (template) => !Array.isArray(template.json_config?.messages) || template.json_config.messages.length <= 1
  );
  const selectedTemplateId = selectedTemplate ? parseInt(selectedTemplate, 10) : null;
  const selectedTemplateMeta = templates.find(
    (template) => template.id === selectedTemplateId
  );
  const selectedTemplateIsSingle = selectedTemplateMeta
    ? !Array.isArray(selectedTemplateMeta.json_config?.messages) ||
      selectedTemplateMeta.json_config.messages.length <= 1
    : false;
  const resolvedFromEmail = (() => {
    const firstMessage = selectedTemplateMeta?.json_config?.messages?.[0];
    if (!firstMessage) return null;
    const fe = firstMessage.from_email || '';
    const fd = firstMessage.from_domain || '';
    if (fe && !fe.includes('@') && fd) return `${fe}@${fd}`;
    return fe || null;
  })();

  // Load template content when template is selected and customization is enabled
  useEffect(() => {
    if (selectedTemplateMeta && customizeTemplate) {
      const messages = selectedTemplateMeta.json_config?.messages;

      if (messages && messages.length > 0) {
        // Multi-message template - load all messages
        setCustomMessages(messages.map(msg => ({
          subject: msg.subject || '',
          body: msg.body || '',
          type: msg.type || 'email'
        })));
        setCustomSubject(''); // Clear single fields
        setCustomBody('');
      } else {
        // Single-message template
        setCustomSubject(selectedTemplateMeta.subject || '');
        setCustomBody(selectedTemplateMeta.content || selectedTemplateMeta.body || '');
        setCustomMessages([]);
      }
    } else {
      setCustomSubject('');
      setCustomBody('');
      setCustomMessages([]);
    }
  }, [selectedTemplate, customizeTemplate, selectedTemplateMeta]);

  // Fetch existing ramp state for the resolved sending identity
  // When the template has no From address the server uses the Settings sender;
  // the advice card reports that address so the ramp lock still applies.
  const rampLookupEmail = resolvedFromEmail || adviceFromEmail;
  useEffect(() => {
    if (!rampLookupEmail) {
      setExistingRampState(null);
      return undefined;
    }
    let cancelled = false;
    sendingIntelligenceAPI.getRampState(rampLookupEmail)
      .then((response) => { if (!cancelled) setExistingRampState(response.data.state); })
      .catch(() => { if (!cancelled) setExistingRampState(null); });
    return () => { cancelled = true; };
  }, [rampLookupEmail]);

  // Ramp belongs to the domain, not the campaign — if the resolved identity
  // is already ramping, force the toggle on so the submitted payload keeps
  // ramp_enabled: true even though the user didn't personally check it.
  useEffect(() => {
    if (existingRampState && existingRampState.enabled) {
      setRampEnabled(true);
    }
  }, [existingRampState]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoadingData(true);

      // Load templates (alphabetically sorted in backend)
      const templatesResponse = await templatesAPI.getAll({ sort_alpha: true });
      const allTemplates = Array.isArray(templatesResponse.data.templates)
        ? templatesResponse.data.templates
        : (Array.isArray(templatesResponse.data) ? templatesResponse.data : []);
      setTemplates(allTemplates);

      // Load groups (alphabetically sorted)
      const groupsResponse = await buyerGroupsAPI.getAll();
      // Sort groups alphabetically
      const sortedGroups = (groupsResponse.data.groups || []).sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
      );
      setGroups(sortedGroups);
    } catch (err) {
      console.error('Failed to load data:', err);
      setError('Failed to load templates and groups');
    } finally {
      setLoadingData(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!campaignName.trim()) {
      setError('Please enter a campaign name');
      return;
    }

    if (!isEditMode) {
      if (!selectedTemplate) {
        setError('Please select a template');
        return;
      }

      if (!selectedGroup) {
        setError('Please select a group');
        return;
      }
    }

    try {
      setLoading(true);
      setError('');

      const campaignData = {
        name: campaignName,
        description,
        start_time: scheduledStartTime || null
      };

      campaignData.ramp_enabled = rampEnabled;
      campaignData.prioritize_deliverable_enabled = prioritizeEnabled;

      // Only include template_id and group_id for create mode, or group_id for draft campaigns
      if (!isEditMode) {
        campaignData.template_id = parseInt(selectedTemplate, 10);
        campaignData.group_id = parseInt(selectedGroup, 10);

        // Include preview template overrides if set
        if (previewTemplateOverrides) {
          campaignData.template_overrides = previewTemplateOverrides;
        }
      } else if (campaign?.status === 'draft') {
        // Allow changing group for draft campaigns
        campaignData.group_id = parseInt(selectedGroup, 10);
      }

      const response = isEditMode
        ? await campaignsAPI.update(campaign.id, campaignData)
        : await campaignsAPI.create(campaignData);

      if (response.data.success) {
        onSuccess(isEditMode ? campaign.id : response.data.campaign_id);
      } else {
        setError(response.data.error || `Failed to ${isEditMode ? 'update' : 'create'} campaign`);
      }
    } catch (err) {
      console.error('Error creating campaign:', err);
      setError(err.response?.data?.error || 'Failed to create campaign. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getTemplateLabel = (template) => {
    const messageCount = template.json_config?.messages?.length || 0;
    if (messageCount > 0) {
      return `${template.name} (${messageCount} ${messageCount === 1 ? 'message' : 'messages'})`;
    }
    const templateType = (template.type || template.template_type || 'email').toUpperCase();
    return `${template.name} (${templateType} • single message)`;
  };

  if (loadingData) {
    return (
      <div className="simple-campaign-form">
        <div className="loading-state">Loading...</div>
      </div>
    );
  }

  return (
    <div className="simple-campaign-form">
      {error && <div className="alert alert-danger">{error}</div>}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Campaign Name *</label>
          <input
            type="text"
            value={campaignName}
            onChange={(e) => setCampaignName(e.target.value)}
            placeholder="e.g., Q4 Real Estate Outreach"
            required
          />
        </div>

        <div className="form-group">
          <label>Select Group *</label>
          <select
            value={selectedGroup}
            onChange={(e) => setSelectedGroup(e.target.value)}
            required
            disabled={isEditMode && campaign?.status !== 'draft'}
          >
            <option value="">-- Choose a buyer group --</option>
            {groups.map(group => (
              <option key={group.id} value={group.id}>
                {group.name} ({group.buyer_count || 0} buyers)
              </option>
            ))}
          </select>
          <small className="text-muted">
            {isEditMode && campaign?.status !== 'draft'
              ? 'Group cannot be changed after campaign is launched'
              : 'Campaign will target all buyers in this group'}
          </small>
        </div>

        <div className="form-group">
          <label>Select Template *</label>
          <select
            value={selectedTemplate}
            onChange={(e) => setSelectedTemplate(e.target.value)}
            required
            disabled={isEditMode}
          >
            <option value="">-- Choose Template --</option>
            {multiTemplates.length > 0 && (
              <optgroup label="Multi-message sequences" key="multi">
                {multiTemplates.map(template => (
                  <option key={template.id} value={template.id}>
                    {getTemplateLabel(template)}
                  </option>
                ))}
              </optgroup>
            )}
            {singleTemplates.length > 0 && (
              <optgroup label="Single-message templates" key="single">
                {singleTemplates.map(template => (
                  <option key={template.id} value={template.id}>
                    {getTemplateLabel(template)}
                  </option>
                ))}
              </optgroup>
            )}
          </select>
          {templates.length === 0 ? (
            <small className="text-muted" style={{ color: 'var(--danger)' }}>
              No templates available. Please create a template first.
            </small>
          ) : (
            <small className="text-muted">
              {isEditMode
                ? 'Template cannot be changed after campaign creation'
                : selectedTemplate
                  ? (selectedTemplateIsSingle
                      ? 'Single-message template selected: campaign will send one message per buyer.'
                      : 'Multi-message sequence selected: campaign will use the defined cadence.')
                  : 'Template defines message content and timing for the campaign.'}
            </small>
          )}
        </div>

        <SendingAdviceCard
          ready={!!selectedTemplate}
          fromEmail={resolvedFromEmail || undefined}
          groupId={selectedGroup}
          onAdvice={(advice) => setAdviceFromEmail(advice?.from_email || null)}
          rampLocked={!!(existingRampState && existingRampState.enabled)}
          rampEnabled={rampEnabled}
          showGroup={!(isEditMode && campaign?.status !== 'draft')}
          onApply={({ ramp, prioritize }) => {
            if (ramp !== null) setRampEnabled(ramp);
            if (prioritize !== null) setPrioritizeEnabled(prioritize);
          }}
        />

        <div className="form-group">
          <label>
            <input
              type="checkbox"
              checked={rampEnabled}
              onChange={(e) => setRampEnabled(e.target.checked)}
              disabled={!!(existingRampState && existingRampState.enabled)}
            />
            {' '}Ramp up this sending domain
          </label>
          {existingRampState && existingRampState.enabled ? (
            <small className="text-muted" style={{ display: 'block' }}>
              {rampLookupEmail} is already ramping (started {new Date(existingRampState.ramp_started_at).toLocaleDateString()}).
              This campaign will automatically use its current ramp state.
            </small>
          ) : (
            <small className="text-muted" style={{ display: 'block' }}>
              Gradually increases daily send volume for a new sending domain instead of sending to everyone at once.
            </small>
          )}
        </div>

        <div className="form-group">
          <label>
            <input
              type="checkbox"
              checked={prioritizeEnabled}
              onChange={(e) => setPrioritizeEnabled(e.target.checked)}
            />
            {' '}Prioritize deliverable emails
          </label>
          <small className="text-muted" style={{ display: 'block' }}>
            Sends to verified/deliverable addresses first. Addresses Hunter has flagged as rejected are excluded automatically.
          </small>
        </div>

        <div className="form-group">
          <label>Description (Optional)</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional campaign description"
            rows={2}
          />
        </div>

        <div className="form-group">
          <label>Schedule Start Time (Optional)</label>
          <input
            type="datetime-local"
            value={scheduledStartTime}
            onChange={(e) => setScheduledStartTime(e.target.value)}
            min={new Date().toISOString().slice(0, 16)}
          />
          <small className="text-muted">
            {scheduledStartTime
              ? `Campaign will start at ${new Date(scheduledStartTime).toLocaleString()}`
              : 'Leave empty to start immediately when launched'}
          </small>
        </div>

        {selectedTemplate && onOpenTemplateEditor && (
          <div className="form-group">
            <label>Template Customisation</label>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                if (isEditMode) {
                  onOpenTemplateEditor();
                } else {
                  onOpenTemplateEditor(parseInt(selectedTemplate, 10));
                }
              }}
              style={{ width: '100%', marginTop: '8px' }}
            >
              {(campaign?.template_overrides || previewTemplateOverrides) ? 'Edit Custom Template' : 'Customise Template'}
            </button>
            {(campaign?.template_overrides || previewTemplateOverrides) && (
              <small className="text-muted" style={{ display: 'block', marginTop: '8px', color: 'var(--success)' }}>
                ✓ Template customisations will be applied
              </small>
            )}
          </div>
        )}

        {false && customizeTemplate && !isEditMode && customMessages.length > 0 && (
          <div style={{ backgroundColor: 'var(--surface-subtle)', padding: '16px', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
            <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600 }}>
              Customise Messages ({customMessages.length} messages)
            </h4>
            {customMessages.map((msg, index) => (
              <div key={index} style={{ marginBottom: index < customMessages.length - 1 ? '20px' : '0', paddingBottom: index < customMessages.length - 1 ? '20px' : '0', borderBottom: index < customMessages.length - 1 ? '1px solid var(--border-color)' : 'none' }}>
                <div style={{ fontWeight: 600, marginBottom: '12px', color: 'var(--text-primary)' }}>
                  Message {index + 1}
                </div>
                <div className="form-group" style={{ marginBottom: '12px' }}>
                  <label>Subject</label>
                  <input
                    type="text"
                    value={msg.subject}
                    onChange={(e) => {
                      const updated = [...customMessages];
                      updated[index].subject = e.target.value;
                      setCustomMessages(updated);
                    }}
                    placeholder="Email subject line"
                  />
                </div>
                <div className="form-group">
                  <label>Body</label>
                  <textarea
                    value={msg.body}
                    onChange={(e) => {
                      const updated = [...customMessages];
                      updated[index].body = e.target.value;
                      setCustomMessages(updated);
                    }}
                    placeholder="Message content..."
                    rows={6}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid var(--border-strong)',
                      borderRadius: '4px',
                      fontFamily: 'inherit',
                      resize: 'vertical'
                    }}
                  />
                </div>
              </div>
            ))}
            <small className="text-muted" style={{ display: 'block', marginTop: '12px' }}>
              You can use placeholders like {'{business_name}'}, {'{industry}'}, {'{location}'}
            </small>
          </div>
        )}

        {false && customizeTemplate && !isEditMode && customMessages.length === 0 && (
          <>
            <div className="form-group">
              <label>Custom Subject</label>
              <input
                type="text"
                value={customSubject}
                onChange={(e) => setCustomSubject(e.target.value)}
                placeholder="Email subject line"
              />
            </div>

            <div className="form-group">
              <label>Custom Message Body</label>
              <textarea
                value={customBody}
                onChange={(e) => setCustomBody(e.target.value)}
                placeholder="Message content..."
                rows={8}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid var(--border-strong)',
                  borderRadius: '4px',
                  fontFamily: 'inherit',
                  resize: 'vertical'
                }}
              />
              <small className="text-muted">
                You can use placeholders like {'{business_name}'}, {'{industry}'}, {'{location}'}
              </small>
            </div>
          </>
        )}

        <div className="form-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update Campaign' : 'Create Campaign')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SimpleCampaignForm;
